// src/pages/admin/AdminFinancePage.jsx
// Phase 4 — M4: Admin Finance Command Centre
// Credit approvals, BNPL stats, invoice discounting approvals, ledger

import { useEffect, useState } from 'react'
import { financeApi } from '../../api/services'

const C = { pink: '#FF3D7F', orange: '#FF8500', ink: '#1A1A2E', muted: '#9CA3AF', green: '#10B981', red: '#EF4444', amber: '#F59E0B', bg: '#F8F9FA' }

function Stat({ icon, label, value, accent }) {
  return (
    <div style={{ background: '#fff', borderRadius: 16, padding: '20px', border: '1px solid #F0F0F0' }}>
      <p style={{ fontSize: '1.4rem', margin: '0 0 8px' }}>{icon}</p>
      <p style={{ fontFamily: 'Playfair Display, serif', fontWeight: 800, fontSize: '1.6rem', color: accent ?? C.ink, margin: 0 }}>{value}</p>
      <p style={{ color: C.muted, fontSize: '0.74rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em', margin: '4px 0 0' }}>{label}</p>
    </div>
  )
}

function Pill({ label, color }) {
  return <span style={{ background: `${color}18`, color, padding: '2px 10px', borderRadius: 20, fontSize: '0.72rem', fontWeight: 700 }}>{label}</span>
}

function SectionTitle({ children }) {
  return <h2 style={{ fontFamily: 'Playfair Display, serif', fontWeight: 800, fontSize: '1.2rem', color: C.ink, margin: '28px 0 14px' }}>{children}</h2>
}

export default function AdminFinancePage() {
  const [data, setData]       = useState(null)
  const [loading, setLoading] = useState(true)
  const [tab, setTab]         = useState('overview')
  const [action, setAction]   = useState(null)   // { type, id, extra }
  const [form, setForm]       = useState({})

  const load = () => {
    setLoading(true)
    financeApi.admin.overview()
      .then(r => setData(r.data?.data ?? r.data))
      .finally(() => setLoading(false))
  }
  useEffect(load, [])

  async function approveCredit(buyerId) {
    const { credit_limit, repayment_days, max_installments, notes } = form
    if (!credit_limit) return alert('Credit limit is required.')
    try {
      await financeApi.admin.approveCredit(buyerId, { credit_limit: +credit_limit, repayment_days: +(repayment_days||30), max_installments: +(max_installments||3), notes })
      setAction(null); load()
    } catch(e) { alert(e.response?.data?.message ?? 'Failed') }
  }

  async function approveIDR(id) {
    try { await financeApi.admin.approveDiscountRequest(id); load() } catch(e) { alert('Failed') }
  }

  async function disburseIDR(id) {
    const utr = prompt('Enter UTR / bank reference for disbursement:')
    if (!utr) return
    try { await financeApi.admin.disburseDiscountRequest(id, utr); load() } catch(e) { alert('Failed') }
  }

  async function rejectIDR(id) {
    const reason = prompt('Rejection reason:')
    if (!reason) return
    try { await financeApi.admin.rejectDiscountRequest(id, reason); load() } catch(e) { alert('Failed') }
  }

  if (loading) return <div style={{ color: C.muted, textAlign: 'center', padding: 60 }}>Loading finance data…</div>

  const credit = data?.credit ?? {}
  const bnpl   = data?.bnpl   ?? {}
  const idr    = data?.idr    ?? {}

  const tabs = [['overview','📊 Overview'],['credit','💳 Credit Accounts'],['idr','🏦 Invoice Discounting'],['transactions','📋 Ledger']]

  return (
    <div>
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontFamily: 'Playfair Display, serif', fontWeight: 800, fontSize: '2rem', color: C.ink, margin: '0 0 4px' }}>Finance Command Centre</h1>
        <p style={{ color: C.muted, margin: 0, fontSize: '0.88rem' }}>BNPL portfolio, credit approvals, invoice discounting, and finance ledger.</p>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24, background: '#fff', borderRadius: 14, padding: 5, border: '1px solid #F0F0F0', width: 'fit-content', flexWrap: 'wrap' }}>
        {tabs.map(([key, lbl]) => (
          <button key={key} onClick={() => setTab(key)} style={{
            padding: '8px 16px', borderRadius: 10, fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer', border: 'none',
            background: tab === key ? `linear-gradient(135deg, ${C.pink}, ${C.orange})` : 'transparent',
            color: tab === key ? '#fff' : C.muted,
          }}>{lbl}</button>
        ))}
      </div>

      {/* ── OVERVIEW ─────────────────────────────────────────────────────── */}
      {tab === 'overview' && (
        <div>
          <SectionTitle>Credit Portfolio</SectionTitle>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 14, marginBottom: 24 }}>
            <Stat icon="👥" label="Active Accounts" value={credit.active_accounts ?? 0} />
            <Stat icon="💸" label="Total Outstanding" value={`₹${(credit.total_outstanding ?? 0).toLocaleString('en-IN')}`} accent={C.red} />
            <Stat icon="📊" label="Utilisation" value={`${credit.utilisation_pct ?? 0}%`} accent={C.amber} />
            <Stat icon="⏳" label="Pending KYC" value={credit.pending_kyc ?? 0} accent={credit.pending_kyc > 0 ? C.amber : C.ink} />
          </div>

          <SectionTitle>BNPL Portfolio</SectionTitle>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 14, marginBottom: 24 }}>
            <Stat icon="✅" label="Active BNPL" value={bnpl.active_bnpl_orders ?? 0} />
            <Stat icon="🚨" label="Overdue Orders" value={bnpl.overdue_bnpl_orders ?? 0} accent={bnpl.overdue_bnpl_orders > 0 ? C.red : C.ink} />
            <Stat icon="💰" label="Outstanding" value={`₹${(bnpl.total_outstanding ?? 0).toLocaleString('en-IN')}`} />
            <Stat icon="💳" label="Fees This Month" value={`₹${(bnpl.fees_this_month ?? 0).toLocaleString('en-IN')}`} accent={C.green} />
          </div>

          <SectionTitle>Invoice Discounting</SectionTitle>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 14 }}>
            <Stat icon="⏳" label="Pending Requests" value={idr.pending_requests ?? 0} accent={idr.pending_requests > 0 ? C.amber : C.ink} />
            <Stat icon="✅" label="Disbursed (Month)" value={idr.disbursed_this_month ?? 0} />
            <Stat icon="💵" label="Volume (Month)" value={`₹${(idr.volume_disbursed_month ?? 0).toLocaleString('en-IN')}`} />
            <Stat icon="🏦" label="Fees Earned (Month)" value={`₹${(idr.fees_earned_month ?? 0).toLocaleString('en-IN')}`} accent={C.green} />
          </div>

          {/* Top borrowers */}
          {credit.top_borrowers?.length > 0 && (
            <>
              <SectionTitle>Top Borrowers</SectionTitle>
              <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #F0F0F0', overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem' }}>
                  <thead>
                    <tr style={{ background: '#FAFAFA', borderBottom: '1px solid #F0F0F0' }}>
                      {['Buyer','Used Credit','Limit','Utilisation'].map(h => <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 700, color: C.muted, fontSize: '0.75rem', textTransform: 'uppercase' }}>{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {credit.top_borrowers.map(b => (
                      <tr key={b.buyer_id} style={{ borderBottom: '1px solid #F9F9F9' }}>
                        <td style={{ padding: '12px 16px', fontWeight: 700, color: C.ink }}>{b.buyer?.name} <span style={{ color: C.muted, fontWeight: 400, fontSize: '0.76rem' }}>{b.buyer?.email}</span></td>
                        <td style={{ padding: '12px 16px', color: C.red, fontWeight: 700 }}>₹{b.used_credit?.toLocaleString('en-IN')}</td>
                        <td style={{ padding: '12px 16px' }}>₹{b.credit_limit?.toLocaleString('en-IN')}</td>
                        <td style={{ padding: '12px 16px' }}>
                          <div style={{ background: '#F0F0F0', borderRadius: 4, height: 6, width: 100 }}>
                            <div style={{ height: '100%', width: `${Math.min(100,(b.used_credit/b.credit_limit)*100)}%`, background: C.orange, borderRadius: 4 }} />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── CREDIT ACCOUNTS ────────────────────────────────────────────────── */}
      {tab === 'credit' && <CreditAccountsTab onApprove={id => { setAction({ type: 'approve_credit', id }); setForm({}) }} />}

      {/* ── INVOICE DISCOUNTING ─────────────────────────────────────────────── */}
      {tab === 'idr' && (
        <IDRTab
          onApprove={approveIDR}
          onDisburse={disburseIDR}
          onReject={rejectIDR}
        />
      )}

      {/* ── LEDGER ─────────────────────────────────────────────────────────── */}
      {tab === 'transactions' && <LedgerTab />}

      {/* ── Credit Approval Modal ──────────────────────────────────────────── */}
      {action?.type === 'approve_credit' && (
        <Modal title="Approve Credit Account" onClose={() => setAction(null)}>
          <FormRow label="Credit Limit (₹)"><input type="number" value={form.credit_limit||''} onChange={e => setForm(f=>({...f,credit_limit:e.target.value}))} placeholder="e.g. 100000" style={inputStyle} /></FormRow>
          <FormRow label="Repayment Days"><input type="number" value={form.repayment_days||30} onChange={e => setForm(f=>({...f,repayment_days:e.target.value}))} style={inputStyle} /></FormRow>
          <FormRow label="Max Installments"><input type="number" value={form.max_installments||3} onChange={e => setForm(f=>({...f,max_installments:e.target.value}))} style={inputStyle} /></FormRow>
          <FormRow label="Notes (optional)"><input value={form.notes||''} onChange={e => setForm(f=>({...f,notes:e.target.value}))} placeholder="Internal notes" style={inputStyle} /></FormRow>
          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <button onClick={() => setAction(null)} style={{ flex: 1, padding: 11, borderRadius: 12, border: '1.5px solid #E5E7EB', background: '#fff', color: C.muted, fontWeight: 700, cursor: 'pointer', fontSize: '0.88rem' }}>Cancel</button>
            <button onClick={() => approveCredit(action.id)} style={{ flex: 2, padding: 11, borderRadius: 12, border: 'none', background: `linear-gradient(135deg, ${C.pink}, ${C.orange})`, color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: '0.88rem' }}>Approve & Notify Buyer</button>
          </div>
        </Modal>
      )}
    </div>
  )
}

// ── Sub-page components ─────────────────────────────────────────────────────

function CreditAccountsTab({ onApprove }) {
  const [accounts, setAccounts] = useState([])
  const [loading, setLoading]   = useState(true)
  const [filter, setFilter]     = useState('pending')

  useEffect(() => {
    setLoading(true)
    financeApi.admin.creditAccounts({ kyc_status: filter === 'pending' ? 'pending' : undefined, status: filter !== 'pending' ? filter : undefined })
      .then(r => setAccounts(r.data?.data?.data ?? r.data?.data ?? []))
      .finally(() => setLoading(false))
  }, [filter])

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 18 }}>
        {['pending','active','suspended'].map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{
            padding: '7px 14px', borderRadius: 20, border: `1.5px solid ${filter===f ? C.pink : '#E5E7EB'}`,
            background: filter===f ? '#FFF0F5' : '#fff', color: filter===f ? C.pink : C.muted,
            fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer', textTransform: 'capitalize',
          }}>{f}</button>
        ))}
      </div>

      {loading ? <div style={{ color: C.muted, padding: 40, textAlign: 'center' }}>Loading…</div>
        : accounts.length === 0 ? <div style={{ color: C.muted, padding: 40, textAlign: 'center', background: '#fff', borderRadius: 16, border: '1px solid #F0F0F0' }}>No accounts in this category.</div>
        : (
          <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #F0F0F0', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem' }}>
              <thead>
                <tr style={{ background: '#FAFAFA', borderBottom: '1px solid #F0F0F0' }}>
                  {['Buyer','Credit Limit','Used','Available','Status','KYC','Action'].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, color: C.muted, fontSize: '0.74rem', textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {accounts.map(a => (
                  <tr key={a.id} style={{ borderBottom: '1px solid #F9F9F9' }}>
                    <td style={{ padding: '12px 14px' }}><span style={{ fontWeight: 700, color: '#1A1A2E' }}>{a.buyer?.name}</span><br /><span style={{ color: C.muted, fontSize: '0.74rem' }}>{a.buyer?.email}</span></td>
                    <td style={{ padding: '12px 14px' }}>₹{(a.credit_limit||0).toLocaleString('en-IN')}</td>
                    <td style={{ padding: '12px 14px', color: C.red, fontWeight: 700 }}>₹{(a.used_credit||0).toLocaleString('en-IN')}</td>
                    <td style={{ padding: '12px 14px', color: C.green, fontWeight: 700 }}>₹{(a.available_credit||0).toLocaleString('en-IN')}</td>
                    <td style={{ padding: '12px 14px' }}><Pill label={a.status} color={a.status==='active'?C.green:a.status==='suspended'?C.red:C.muted} /></td>
                    <td style={{ padding: '12px 14px' }}><Pill label={a.kyc_status} color={a.kyc_status==='verified'?C.green:a.kyc_status==='pending'?C.amber:C.red} /></td>
                    <td style={{ padding: '12px 14px' }}>
                      {(a.kyc_status === 'pending' || a.status === 'inactive') && (
                        <button onClick={() => onApprove(a.buyer_id)} style={{ padding: '5px 12px', borderRadius: 8, border: 'none', background: C.green, color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: '0.77rem' }}>Approve</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      }
    </div>
  )
}

function IDRTab({ onApprove, onDisburse, onReject }) {
  const [reqs, setReqs]       = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter]   = useState('pending')

  useEffect(() => {
    setLoading(true)
    financeApi.admin.discountRequests({ status: filter })
      .then(r => setReqs(r.data?.data?.data ?? r.data?.data ?? []))
      .finally(() => setLoading(false))
  }, [filter])

  const statusColor = { pending: C.amber, approved: '#3B82F6', disbursed: C.green, rejected: C.red }

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 18 }}>
        {['pending','approved','disbursed','rejected'].map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{
            padding: '7px 14px', borderRadius: 20, border: `1.5px solid ${filter===f ? C.pink : '#E5E7EB'}`,
            background: filter===f ? '#FFF0F5' : '#fff', color: filter===f ? C.pink : C.muted,
            fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer', textTransform: 'capitalize',
          }}>{f}</button>
        ))}
      </div>

      {loading ? <div style={{ color: C.muted, padding: 40, textAlign: 'center' }}>Loading…</div>
        : reqs.length === 0 ? <div style={{ color: C.muted, padding: 40, textAlign: 'center', background: '#fff', borderRadius: 16, border: '1px solid #F0F0F0' }}>No requests.</div>
        : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {reqs.map(req => (
              <div key={req.id} style={{ background: '#fff', borderRadius: 16, border: '1px solid #F0F0F0', padding: '18px 20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 10 }}>
                  <div>
                    <p style={{ fontWeight: 800, color: C.ink, margin: '0 0 2px' }}>{req.request_number}</p>
                    <p style={{ color: C.muted, fontSize: '0.78rem', margin: 0 }}>
                      {req.supplier?.business_name ?? req.supplier?.name} · Order {req.order?.order_number}
                    </p>
                  </div>
                  <Pill label={req.status} color={statusColor[req.status]??C.muted} />
                </div>
                <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', fontSize: '0.82rem', marginBottom: 12, color: C.muted }}>
                  <span>Invoice: <b style={{ color: C.ink }}>₹{req.invoice_amount?.toLocaleString('en-IN')}</b></span>
                  <span>Fee ({req.discount_fee_pct}%): <b style={{ color: C.red }}>₹{req.discount_fee?.toLocaleString('en-IN')}</b></span>
                  <span>Advance: <b style={{ color: C.green }}>₹{req.advance_amount?.toLocaleString('en-IN')}</b></span>
                </div>
                {req.status === 'pending' && (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => onApprove(req.id)} style={{ padding: '6px 14px', borderRadius: 8, border: 'none', background: C.green, color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: '0.8rem' }}>✅ Approve</button>
                    <button onClick={() => onReject(req.id)} style={{ padding: '6px 14px', borderRadius: 8, border: '1.5px solid #E5E7EB', background: '#fff', color: C.red, fontWeight: 700, cursor: 'pointer', fontSize: '0.8rem' }}>✗ Reject</button>
                  </div>
                )}
                {req.status === 'approved' && (
                  <button onClick={() => onDisburse(req.id)} style={{ padding: '6px 14px', borderRadius: 8, border: 'none', background: '#3B82F6', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: '0.8rem' }}>💸 Mark Disbursed</button>
                )}
              </div>
            ))}
          </div>
        )
      }
    </div>
  )
}

function LedgerTab() {
  const [txns, setTxns]     = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    financeApi.admin.transactions()
      .then(r => setTxns(r.data?.data?.data ?? r.data?.data ?? []))
      .finally(() => setLoading(false))
  }, [])

  if (loading) return <div style={{ color: C.muted, textAlign: 'center', padding: 40 }}>Loading ledger…</div>

  return (
    <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #F0F0F0', overflow: 'hidden' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem' }}>
        <thead>
          <tr style={{ background: '#FAFAFA', borderBottom: '1px solid #F0F0F0' }}>
            {['Date','User','Type','Amount','Dir','Description'].map(h => (
              <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, color: C.muted, fontSize: '0.74rem', textTransform: 'uppercase' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {txns.length === 0 ? (
            <tr><td colSpan={6} style={{ padding: 40, textAlign: 'center', color: C.muted }}>No transactions yet.</td></tr>
          ) : txns.map(t => (
            <tr key={t.id} style={{ borderBottom: '1px solid #F9F9F9' }}>
              <td style={{ padding: '10px 14px', color: C.muted, fontSize: '0.77rem' }}>{new Date(t.created_at).toLocaleDateString('en-IN')}</td>
              <td style={{ padding: '10px 14px', fontWeight: 600, color: C.ink }}>{t.user?.name}</td>
              <td style={{ padding: '10px 14px' }}><Pill label={t.type.replace(/_/g,' ')} color={C.muted} /></td>
              <td style={{ padding: '10px 14px', fontWeight: 800, color: t.direction==='credit' ? C.green : C.red }}>₹{(+t.amount).toLocaleString('en-IN')}</td>
              <td style={{ padding: '10px 14px' }}><Pill label={t.direction} color={t.direction==='credit' ? C.green : C.red} /></td>
              <td style={{ padding: '10px 14px', color: C.muted, fontSize: '0.78rem', maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.description}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ── Tiny helpers ─────────────────────────────────────────────────────────────

function Modal({ title, children, onClose }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 20, padding: 28, maxWidth: 460, width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
          <h3 style={{ fontFamily: 'Playfair Display, serif', fontWeight: 800, color: C.ink, margin: 0 }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.3rem', cursor: 'pointer', color: C.muted }}>×</button>
        </div>
        {children}
      </div>
    </div>
  )
}

function FormRow({ label, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontWeight: 700, fontSize: '0.78rem', color: '#374151', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</label>
      {children}
    </div>
  )
}

const inputStyle = { width: '100%', padding: '10px 12px', border: '1.5px solid #E5E7EB', borderRadius: 10, fontSize: '0.88rem', outline: 'none', boxSizing: 'border-box' }
